import { useContext, useMemo, useRef } from 'react'

import { Button, useHealthToolsCache } from '@hhgtech/hhg-components'
import { DatePicker, Slider, Text } from '@hhgtech/hhg-components/mantine'
import { Flex, Stack, rem } from '@mantine/core'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import AdditionalQuestionGroup, {
  HandlerProps as AdditionalQuestionHandlerProps,
} from 'components/atoms/healthTools/form/additionalQuestionGroup'
import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import {
  OvulationFormField,
  useOvulationFormContext,
} from 'components/pages/healthTools/ovulation/context'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import type { AdditionalQuestionFieldType } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import {
  StyledCategoryCard,
  StyledFormControl,
  StyledLastPeriodFormContainer,
  StyledMethodCardWrapper,
  StyledMethodContainer,
  StyledMethodSwitcherContainer,
} from './index.styled'

export enum OvulationCalculatorType {
  TrackPeriod = 1,
  Conceive,
  AvoidPregnancy,
}

const OvulationCalculator = ({
  additionalQuestions = [],
  onCalc,
}: {
  additionalQuestions?: AdditionalQuestionFieldType[]
  onCalc: () => void
}) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const {
    state: { userInfo },
  } = useContext(AppContext)

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const additionQuestionRef = useRef<AdditionalQuestionHandlerProps>(null)

  const linkTool = useMemo(
    () => getHealthToolLink(HEALTH_TOOL_TYPE.OVULATION),
    [getHealthToolLink],
  )
  const calculatingMethods = useMemo(
    () => [
      {
        imageSrc: `${assetImagePath}/ovulation-track-period-icon.png`,
        label: f({ id: 'ovulationPage.trackPeriod' }),
        value: OvulationCalculatorType.TrackPeriod,
      },
      {
        imageSrc: `${assetImagePath}/ovulation-conceive-icon.png`,
        label: f({ id: 'ovulationPage.conceive' }),
        value: OvulationCalculatorType.Conceive,
      },
      {
        imageSrc: `${assetImagePath}/ovulation-avoid-pregnancy-icon.png`,
        label: f({ id: 'ovulationPage.avoidPregnancy' }),
        value: OvulationCalculatorType.AvoidPregnancy,
      },
    ],
    [],
  )

  const form = useOvulationFormContext()
  const onSubmit = async (values: OvulationFormField) => {
    additionQuestionRef?.current?.validate(async () => {
      localStorage.setItem(
        'ovulationInfo',
        JSON.stringify({
          ...values,
          isSubmitted: Boolean(userInfo?.id),
          source_url: window.location.href,
        }),
      )
      onCalc?.()
    })
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Flex
        gap={rem(24)}
        sx={(theme) => ({
          [theme.fn.smallerThan('sm')]: {
            flexDirection: 'column',
            '.birthday-field': {
              marginBottom: 0,
            },
          },
          [theme.fn.largerThan('sm')]: {
            '& > div': {
              width: '100%',
              flexGrow: 1,
            },
          },
        })}
      >
        <StyledFormControl className="birthday-field">
          <AgeInput
            {...form.getInputProps('birthday')}
            onChange={(v) => {
              form.setFieldValue('age', String(dayjs().diff(v, 'year')))
              form.setFieldValue('birthday', v as Date)
            }}
          />
        </StyledFormControl>
        <StyledFormControl>
          <CheckYourSelfSwitching
            checked={form.getInputProps('isYourself').value}
            onChange={(e) =>
              form.setFieldValue('isYourself', e.currentTarget.checked)
            }
          />
        </StyledFormControl>
      </Flex>
      <StyledMethodContainer>
        <Text as="label" size="s4">
          {f({ id: 'ovulationPage.objectives' })}
        </Text>
        <StyledMethodSwitcherContainer>
          {calculatingMethods.map((method) => (
            <StyledMethodCardWrapper
              key={method.value}
              onClick={(e) => {
                e.stopPropagation()
                form.getInputProps('type').onChange(method.value)
              }}
            >
              <StyledCategoryCard
                className={
                  method.value === form.getInputProps('type').value
                    ? 'active'
                    : undefined
                }
                imgBanner={
                  <img
                    src={method.imageSrc}
                    style={{
                      width: '63px',
                      height: '63px',
                      objectFit: 'contain',
                      maxWidth: '100%',
                    }}
                    alt={method.label}
                    loading="lazy"
                  />
                }
                label={method.label}
              />
            </StyledMethodCardWrapper>
          ))}
        </StyledMethodSwitcherContainer>
      </StyledMethodContainer>
      <StyledLastPeriodFormContainer>
        <StyledFormControl>
          <DatePicker
            label={
              <Text as="label" size="s4">
                {f({
                  id: 'dueDatePage.lastPeriod.lastPeriodLabel',
                })}
              </Text>
            }
            excludeDate={(date) =>
              dayjs(date).isBefore(dayjs().subtract(46, 'day')) ||
              dayjs(date).isAfter(dayjs())
            }
            {...form.getInputProps('startDate')}
          />
        </StyledFormControl>

        <StyledFormControl>
          <Text as="label" size="s4">
            {f({ id: 'ovulationPage.cycleLength' }) + ' '}
            <Text size="s4" color="#797979" style={{ display: 'inline' }}>
              {f({ id: 'ovulationPage.dayUnit' })}
            </Text>
          </Text>
          <Slider
            {...form.getInputProps('cycle')}
            withControl
            min={21}
            max={45}
          />
        </StyledFormControl>

        <StyledFormControl>
          <Text size="s4" color="#595959">
            {f({ id: 'ovulationPage.periodDuration' }) + ' '}
            <Text size="s4" color="#797979" style={{ display: 'inline' }}>
              {f({ id: 'ovulationPage.dayUnit' })}
            </Text>
          </Text>
          <Slider
            {...form.getInputProps('periodLength')}
            withControl
            min={1}
            max={15}
          />
        </StyledFormControl>
        <Stack spacing={rem(24)}>
          <AdditionalQuestionGroup
            ref={additionQuestionRef}
            additionalQuestions={additionalQuestions}
            onChange={(v) => form.setFieldValue('additionalQuestions', v)}
          />

          <StyledFormControl>
            <Button
              className="due-date-calculate-button"
              size="lg"
              color="primary"
              dataEventCategory="Health Tool"
              dataEventAction="Ovulation"
              dataEventLabel={`${getHelloSitesUrl(locale)}${linkTool}`}
              type="submit"
            >
              {f({
                id: 'dueDatePage.lastPeriod.calculate',
              })}
            </Button>
          </StyledFormControl>
        </Stack>
      </StyledLastPeriodFormContainer>
    </form>
  )
}

export { OvulationCalculator }
